import { FC, useEffect, useMemo, useState } from "react";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import type {
  ScoredAccount,
  ScorePillar,
  ScoreSubDimension,
  DisqualifierReason,
} from "@/apis/companySearch/aiScoring";

const ACCENT = "#6d28d9"; // violet — pillar dot + score
const ACCENT_BG = "#f4f1fb"; // lavender — pillar header row

// Pillars always render fit → need → intent, regardless of the response's
// key order (the breakdown arrives as a map, so its JSON key order is not
// deterministic). Any unknown pillar sorts to the end, stably.
const PILLAR_ORDER = ["fit", "need", "intent"];
const pillarRank = (key: string): number => {
  const i = PILLAR_ORDER.indexOf(key);
  return i === -1 ? PILLAR_ORDER.length : i;
};

/** "hosting_displacement" → "Hosting Displacement". The response keys pillars/
 * dimensions/sub-dimensions by their raw config keys; reason `label`s are
 * already display-ready. */
function humanizeKey(key: string): string {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/** A sub-dimension counts as "relevant" when it actually contributed points. */
function isRelevantSub(sub: ScoreSubDimension): boolean {
  return (
    (sub.score ?? 0) > 0 || Boolean(sub.reasons?.some((r) => r.points > 0))
  );
}

/** A leaf criterion row — one per sub-dimension, flattened across dimensions. */
interface Leaf {
  key: string;
  label: string;
  sub: ScoreSubDimension;
}

/** Flattens a pillar's dimensions → sub-dimensions into a single criterion list
 * (dropping zero-scoring ones when `onlyRelevant`), matching the flat layout of
 * the reference design. */
function pillarLeaves(pillar: ScorePillar, onlyRelevant: boolean): Leaf[] {
  const leaves: Leaf[] = [];
  for (const [dimKey, dim] of Object.entries(pillar.dimensions ?? {})) {
    for (const [subKey, sub] of Object.entries(dim.sub_dimensions ?? {})) {
      if (onlyRelevant && !isRelevantSub(sub)) continue;
      leaves.push({
        key: `${dimKey}/${subKey}`,
        label: humanizeKey(subKey),
        sub,
      });
    }
  }
  return leaves;
}

/** "18 | 30" — actual points (green when earned) | weight/cap (muted). */
const ActualWeight: FC<{ score: number; max?: number }> = ({ score, max }) => (
  <Typography component="span" sx={{ fontSize: 14, whiteSpace: "nowrap" }}>
    <Box
      component="span"
      sx={{
        fontWeight: 700,
        color: score > 0 ? "success.main" : "text.disabled",
      }}
    >
      {Math.round(score)}
    </Box>
    {max != null && (
      <Box component="span" sx={{ color: "text.disabled" }}>
        {"  |  "}
        {max}
      </Box>
    )}
  </Typography>
);

// ── One criterion (sub-dimension): row + expandable matched reasons ───────────

const CriterionRow: FC<{
  leaf: Leaf;
  expanded: boolean;
  onToggle: () => void;
}> = ({ leaf, expanded, onToggle }) => {
  const { sub } = leaf;
  const reasons = sub.reasons ?? [];
  const hasReasons = reasons.length > 0;
  return (
    <Box sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
      <Box
        onClick={hasReasons ? onToggle : undefined}
        data-testid={`model-score-drawer-criterion-${leaf.key}`}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 2,
          py: 1.25,
          cursor: hasReasons ? "pointer" : "default",
          "&:hover": hasReasons ? { bgcolor: "grey.50" } : undefined,
        }}
      >
        <Typography noWrap sx={{ fontSize: 14, color: "text.primary" }}>
          {leaf.label}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <ActualWeight score={sub.score} max={sub.max_points} />
          {hasReasons &&
            (expanded ? (
              <KeyboardArrowUpIcon
                sx={{ fontSize: 18, color: "text.disabled" }}
              />
            ) : (
              <KeyboardArrowDownIcon
                sx={{ fontSize: 18, color: "text.disabled" }}
              />
            ))}
        </Box>
      </Box>

      {expanded &&
        reasons.map((r, idx) => (
          <Box
            key={idx}
            data-testid={`model-score-drawer-reason-${leaf.key}-${idx}`}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              pl: 4,
              pr: 2,
              py: 0.85,
              bgcolor: "grey.50",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                minWidth: 0,
              }}
            >
              <CheckIcon sx={{ fontSize: 16, color: "success.main" }} />
              <Typography noWrap sx={{ fontSize: 13, color: "text.secondary" }}>
                {r.label}
              </Typography>
            </Box>
            <Typography
              sx={{ fontSize: 13, fontWeight: 700, color: "success.main" }}
            >
              {r.points}
            </Typography>
          </Box>
        ))}
    </Box>
  );
};

// ── One pillar: header row + its flattened criteria ───────────────────────────

const PillarSection: FC<{
  pillarKey: string;
  pillar: ScorePillar;
  leaves: Leaf[];
  expanded: boolean;
  onToggle: () => void;
  expandedSubs: Set<string>;
  onToggleSub: (key: string) => void;
}> = ({
  pillarKey,
  pillar,
  leaves,
  expanded,
  onToggle,
  expandedSubs,
  onToggleSub,
}) => (
  <Box>
    <Box
      onClick={onToggle}
      data-testid={`model-score-drawer-pillar-${pillarKey}`}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2,
        py: 1.25,
        cursor: "pointer",
        bgcolor: ACCENT_BG,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: ACCENT,
            flexShrink: 0,
          }}
        />
        <Typography noWrap sx={{ fontSize: 14, fontWeight: 700 }}>
          {humanizeKey(pillarKey)}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 800, color: ACCENT }}>
          {Math.round(pillar.score)}
        </Typography>
        {expanded ? (
          <KeyboardArrowUpIcon sx={{ fontSize: 18, color: "text.disabled" }} />
        ) : (
          <KeyboardArrowDownIcon
            sx={{ fontSize: 18, color: "text.disabled" }}
          />
        )}
      </Box>
    </Box>

    {expanded &&
      leaves.map((leaf) => (
        <CriterionRow
          key={leaf.key}
          leaf={leaf}
          expanded={expandedSubs.has(`${pillarKey}/${leaf.key}`)}
          onToggle={() => onToggleSub(`${pillarKey}/${leaf.key}`)}
        />
      ))}
  </Box>
);

// ── Disqualified banner ───────────────────────────────────────────────────

const REGION_NAMES = new Intl.DisplayNames("en", { type: "region" });
/** alpha-2 code → country name (falls back to the raw code). */
function countryName(code: string): string {
  try {
    return REGION_NAMES.of(code.trim().toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

/** Expands the country codes in a `geography` disqualifier label to names;
 * every other type keeps its already display-ready label. */
function prettyDisqualifierLabel(d: DisqualifierReason): string | undefined {
  if (d.type !== "geography" || !d.label) return d.label;
  const match = d.label.match(/^based (in|outside) (.+)$/);
  if (!match) return d.label;
  return `based ${match[1]} ${match[2].split(", ").map(countryName).join(", ")}`;
}

/** Rose banner shown when an account was disqualified — mirrors the admin score
 * lookup: "DQ · Disqualified — <reasons>". */
const DisqualifiedBanner: FC<{ reasons?: DisqualifierReason[] }> = ({
  reasons,
}) => {
  const labels = (reasons ?? [])
    .map(prettyDisqualifierLabel)
    .filter((l): l is string => !!l);
  if (labels.length === 0) return null;
  return (
    <Alert
      severity="error"
      icon={false}
      data-testid="model-score-drawer-disqualified"
      sx={{
        borderRadius: 0,
        py: 0.75,
        "& .MuiAlert-message": { fontSize: 13 },
      }}
    >
      <Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>
        DQ · Disqualified —
      </Box>
      {labels.join("; ")}
    </Alert>
  );
};

// ── Main drawer ───────────────────────────────────────────────────────────

interface ModelScoreDrawerProps {
  open: boolean;
  onClose: () => void;
  companyName: string;
  modelName: string;
  loading: boolean;
  error: Error | null;
  result: ScoredAccount | null;
  /** Deep-links out to the argo AI-scoring authoring tool. */
  onChangeScoring?: () => void;
}

export const ModelScoreDrawer: FC<ModelScoreDrawerProps> = ({
  open,
  onClose,
  companyName,
  modelName,
  loading,
  error,
  result,
  onChangeScoring,
}) => {
  const [expandedPillars, setExpandedPillars] = useState<Set<string>>(
    new Set()
  );
  const [expandedSubs, setExpandedSubs] = useState<Set<string>>(new Set());
  const [onlyRelevant, setOnlyRelevant] = useState(true);

  // Expand all pillars by default whenever a new result loads.
  useEffect(() => {
    setExpandedPillars(new Set(Object.keys(result?.pillars ?? {})));
    setExpandedSubs(new Set());
  }, [result]);

  const togglePillar = (key: string) =>
    setExpandedPillars((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleSub = (key: string) =>
    setExpandedSubs((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Pillars with their flattened criteria; pillars left empty drop out.
  const visiblePillars = useMemo(
    () =>
      Object.entries(result?.pillars ?? {})
        .map(([key, pillar]) => ({
          key,
          pillar,
          leaves: pillarLeaves(pillar, onlyRelevant),
        }))
        .filter(({ leaves }) => leaves.length > 0)
        .sort((a, b) => pillarRank(a.key) - pillarRank(b.key)),
    [result, onlyRelevant]
  );

  const hasBreakdown = Object.keys(result?.pillars ?? {}).length > 0;
  const hasScore = result?.composite != null || Boolean(result?.tier);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: { width: 400, p: 0, display: "flex", flexDirection: "column" },
        },
      }}
    >
      {/* Title bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
        }}
      >
        <Typography
          variant="overline"
          sx={{ fontWeight: 700, letterSpacing: 1 }}
        >
          COMPANY SCORE
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close score breakdown"
          data-testid="model-score-drawer-close-btn"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider />

      {/* Company + score summary */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.75,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            noWrap
            sx={{ fontSize: 15, fontWeight: 700 }}
            data-testid="model-score-drawer-company-name"
          >
            {companyName}
          </Typography>
          <Chip
            label={modelName}
            size="small"
            data-testid="model-score-drawer-model-name"
            sx={{
              mt: 0.5,
              maxWidth: "100%",
              borderRadius: 999,
              fontWeight: 600,
              fontSize: 11,
            }}
          />
        </Box>
        {loading ? (
          <Skeleton variant="rounded" width={72} height={40} />
        ) : hasScore ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {result?.tier && (
              <Box
                data-testid="model-score-drawer-tier"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 30,
                  height: 30,
                  px: 0.75,
                  borderRadius: 1.5,
                  bgcolor: "success.light",
                  color: "success.dark",
                  fontWeight: 800,
                  fontSize: 15,
                }}
              >
                {result.tier}
              </Box>
            )}
            {result?.composite != null && (
              <Typography
                data-testid="model-score-drawer-composite"
                sx={{ fontSize: 30, fontWeight: 800, lineHeight: 1 }}
              >
                {Math.round(result.composite)}
              </Typography>
            )}
          </Box>
        ) : null}
      </Box>

      {/* Disqualified reasons */}
      {!loading && <DisqualifiedBanner reasons={result?.disqualified_by} />}

      {/* Column header */}
      {!loading && !error && hasBreakdown && (
        <>
          <Divider />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 0.75,
              bgcolor: "grey.100",
            }}
          >
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  size="small"
                  sx={{ p: 0.5 }}
                  checked={onlyRelevant}
                  onChange={(e) => setOnlyRelevant(e.target.checked)}
                  data-testid="model-score-drawer-relevant-checkbox-btn"
                />
              }
              label={
                <Typography variant="caption" color="text.secondary">
                  Show only relevant
                </Typography>
              }
            />
            <Typography variant="caption" color="text.secondary">
              Actual pts&nbsp;&nbsp;|&nbsp;&nbsp;Weight given
            </Typography>
          </Box>
          <Divider />
        </>
      )}

      {/* Body */}
      {loading ? (
        <Box sx={{ px: 2, py: 2 }} data-testid="model-score-drawer-loading">
          <Skeleton variant="text" width="70%" height={22} />
          <Skeleton variant="text" width="90%" height={22} sx={{ mt: 1 }} />
          <Skeleton variant="text" width="80%" height={22} sx={{ mt: 1 }} />
        </Box>
      ) : error ? (
        <Box sx={{ px: 2, py: 2 }}>
          <Alert severity="error" data-testid="model-score-drawer-error">
            {error.message}
          </Alert>
        </Box>
      ) : visiblePillars.length > 0 ? (
        <Box sx={{ overflowY: "auto" }}>
          {visiblePillars.map(({ key, pillar, leaves }) => (
            <PillarSection
              key={key}
              pillarKey={key}
              pillar={pillar}
              leaves={leaves}
              expanded={expandedPillars.has(key)}
              onToggle={() => togglePillar(key)}
              expandedSubs={expandedSubs}
              onToggleSub={toggleSub}
            />
          ))}
        </Box>
      ) : (
        <Box sx={{ px: 2, py: 2 }} data-testid="model-score-drawer-empty">
          <Typography variant="body2" color="text.secondary">
            {hasBreakdown
              ? "No relevant contributions — every criterion scored zero."
              : hasScore
                ? "No score breakdown is available for this company yet."
                : "This company has no score for this model."}
          </Typography>
        </Box>
      )}

      {/* Footer */}
      {onChangeScoring && (
        <Box
          sx={{ mt: "auto", p: 3, display: "flex", justifyContent: "center" }}
        >
          <Button
            variant="outlined"
            size="large"
            data-testid="model-score-drawer-change-scoring-btn"
            onClick={() => {
              onClose();
              onChangeScoring();
            }}
          >
            CHANGE SCORING
          </Button>
        </Box>
      )}
    </Drawer>
  );
};
